/** Typed view of public/assets/manifest.json written by the pipeline gate (R91, R95). */
export type AssetKind = "building" | "unit" | "zombie" | "prop" | "item" | "emote" | "ui" | "terrain" | "scene" | "smoke";

export interface AssetEntry {
  id: string;
  kind: AssetKind;
  /** Default model (buildings: tier 2, zombies: fresh). */
  model?: string;
  lod1?: string;
  /** Buildings: per tier "1"|"2"|"3"; zombies: per variant. */
  models?: Record<string, string>;
  lod1s?: Record<string, string>;
  icon?: string;
  icon1x?: string;
  portrait?: string;
  tierIcons?: Record<string, string>;
  /** Authored material textures, keyed by their pipeline variant. */
  textures?: Record<string, string>;
  /** Comic panels rendered from the associated native 3D scene. */
  panels?: { "1x": string; "2x": string };
  panelProvenance?: string;
  tris: number;
  lod1Tris?: number;
  clips: string[];
  hash: string;
  iconCoverage?: number;
  portraitCoverage?: number;
}

export interface AssetManifest {
  version: number;
  generatedAt: string;
  count: number;
  assets: Record<string, AssetEntry>;
}

let cached: Promise<AssetManifest> | null = null;

export function loadManifest(fetchImpl: typeof fetch = fetch): Promise<AssetManifest> {
  if (!cached) {
    cached = fetchImpl("/assets/manifest.json").then((r) => {
      if (!r.ok) throw new Error(`manifest ${r.status}`);
      return r.json() as Promise<AssetManifest>;
    });
  }
  return cached;
}

export function resetManifestCache(): void {
  cached = null;
}

const KIND_PREFIX = /^(bld|zmb|unit|prop|itm)\./;

/** File key of an asset: content ids drop their kind prefix, terrain ids (tile.ash.0, ruin) are used as-is. */
export function assetKey(id: string): string {
  return id.replace(KIND_PREFIX, "");
}

export function iconUrl(id: string, size: "1x" | "2x" = "2x", buildingLevel?: number): string {
  // Bound fragments/blueprints share the generic item's authored image.
  const key = assetKey(id.startsWith("itm.") ? id.split(":")[0]! : id);
  if (buildingLevel !== undefined && id.startsWith("bld.")) return `/assets/icons/${key}.t${buildingTier(buildingLevel)}.png`;
  return size === "1x" ? `/assets/icons/${key}@1x.png` : `/assets/icons/${key}.png`;
}

export function portraitUrl(unitId: string): string {
  return `/assets/portraits/${unitId.replace("unit.", "")}.png`;
}

export function textureUrl(id: string, variant: string): string {
  return `/assets/textures/${id}/${variant}.png`;
}

export function panelUrl(id: string, size: "1x" | "2x" = "2x"): string {
  return `/assets/panels/${id}${size === "1x" ? "@1x" : ""}.png`;
}

/** Preload actual catalogue imagery through the same URL constructors as the visible components. */
export function decodeAssetImage(id: string, portrait = false): Promise<void> {
  const picture = new Image();
  picture.src = portrait ? portraitUrl(id) : iconUrl(id);
  return picture.decode();
}

/** Model URL for an asset; buildings take the tier (1–3), zombies the variant. */
export function modelUrl(id: string, variant?: number | string, lod: 0 | 1 = 0): string {
  const key = assetKey(id);
  const suffix = lod === 1 ? ".lod1.glb" : ".glb";
  if (id.startsWith("bld.")) return `/assets/models/${key}.t${variant ?? 2}${suffix}`;
  if (id.startsWith("zmb.")) return `/assets/models/${key}.${variant ?? "fresh"}${suffix}`;
  return `/assets/models/${key}${suffix}`;
}

export function buildingTier(level: number): 1 | 2 | 3 {
  return level >= 21 ? 3 : level >= 11 ? 2 : 1;
}
